import React from 'react';
import type { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from 'atom-components/error-message-from-hook-form';
import Select from 'atom-components/form-fields-for-hook-form/select';
import Heading from 'atom-components/heading';
import TooltipItem from 'atom-components/tooltip-item';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import {
  type MarkItemFormValue,
  type MarkResponseFormValues,
  unrefinedMarkResponseFormSchema
} from 'feature-components/mark-response/mark-response-form/mark-response-form.schema';
import { type AuthorisableMark } from 'policies/entities/mark';
import MarkPolicy from 'policies/mark.policy';
import { useCustomerUser } from 'services/assist-api/entities/customer-user/customer-users';
import { TestInstanceResponseInputType } from 'services/assist-api/entities/test-instance-response/types';
import type { TestRubricItemWithOptions } from 'services/assist-api/entities/test-rubric-item/types';
import { usePageAccess } from 'services/providers/page-access.provider';
import { isInitialLoad } from 'utils/check-query-status';
import { sanitizeText } from 'utils/parsing';
import { rubricItemTooltip } from './mark-item.helpers';
import styles from './mark-item.module.scss';

type Props = {
  rubricItem: TestRubricItemWithOptions;
  responseItemId: number;
  authorisableMark: AuthorisableMark;
  defaultValues: MarkResponseFormValues;
};

export default function MarkItem({
  rubricItem,
  responseItemId,
  authorisableMark,
  defaultValues
}: Props): ReactElement {
  const { authorisableUser } = usePageAccess();
  const { watch, control, setValue, formState } = useFormContext<MarkResponseFormValues>();
  const markItems = watch(unrefinedMarkResponseFormSchema.keyof().Enum.markItems);
  const currentUserMarkerId = watch(
    unrefinedMarkResponseFormSchema.keyof().Enum.currentUserMarkerId
  );
  const activeMarkerId = watch(unrefinedMarkResponseFormSchema.keyof().Enum.activeMarkerId);

  const markItemIsForThisRubricItemAndResponseItem = (mi: MarkItemFormValue): boolean =>
    mi.rubricItemId === rubricItem.id && mi.responseItemId === responseItemId;
  const markItem = markItems.find((mi) => markItemIsForThisRubricItemAndResponseItem(mi));
  const markItemIndex = markItems.findIndex((mi) => markItemIsForThisRubricItemAndResponseItem(mi));
  const savedMarkItem = defaultValues.markItems.find((smi) => smi.id === markItem?.id);

  const markerOrganisationUserQuery = useCustomerUser(savedMarkItem?.marker?.organisationUserId);
  const isLoading = isInitialLoad(markerOrganisationUserQuery);
  const { isError } = markerOrganisationUserQuery;

  const tooltip = rubricItemTooltip(
    MarkPolicy.administrate(authorisableUser, authorisableMark),
    markerOrganisationUserQuery.data?.json,
    activeMarkerId,
    savedMarkItem
  );

  return (
    <div key={rubricItem.id}>
      <Heading level={5} className={styles.rubricItemHeading}>
        {rubricItem.displayName}
      </Heading>
      {!isLoading && isError && <LoadingErrorAlert queries={[markerOrganisationUserQuery]} />}
      <TooltipItem tooltip={isLoading ? 'Loading...' : tooltip}>
        <Select
          id={rubricItem.id.toString()}
          ariaLabel="Select rubric item"
          className={styles.rubricItemSelect}
          control={control}
          name={`markItems.${markItemIndex}.submittedValue`}
          size={rubricItem.testRubricItemOptions.length + 1}
          disabled={!MarkPolicy.edit(authorisableUser, authorisableMark)}
          onChange={(event): void => {
            const selectedRubricItemOption = rubricItem.testRubricItemOptions.find(
              (o) => o.identifier === event.target.value
            );
            setValue(
              `markItems.${markItemIndex}`,
              {
                id: markItem?.id,
                submittedValue: selectedRubricItemOption?.identifier || '',
                rubricItemId: rubricItem.id,
                rubricItemOptionId: selectedRubricItemOption?.id,
                responseItemId,
                // Ensure that the marker of the mark item is set only if they are scoring the item
                // for the first time, or have changed the score from what the previous mark item
                // marker selected. Unmodified mark items should retain the previous mark item
                // marker so that we have a record of which marker scored the item.
                // If there is no current user marker, the marker ID field of the mark item will be
                // set to undefined here and we should assume, when saving, that the mark's active
                // marker should be set as the mark item's marker (this will be allocated/assigned
                // on submit if not already set).
                marker:
                  selectedRubricItemOption?.identifier !== savedMarkItem?.submittedValue
                    ? {
                        id: currentUserMarkerId,
                        // When the marker is changed, the current user marker organisation user ID is
                        // not added to the mark item since we don't need to save this value.
                        organisationUserId: undefined
                      }
                    : savedMarkItem?.marker,
                // Ensure that the input type is only changed from autoscored to manual if the score
                // has changed. If the marker selects a new score and then reselects what the AI
                // chose, the mark item should continue to be considered autoscored.
                inputType:
                  selectedRubricItemOption?.identifier !== savedMarkItem?.submittedValue
                    ? TestInstanceResponseInputType.Manual
                    : savedMarkItem?.inputType || TestInstanceResponseInputType.Manual
              },
              {
                shouldValidate: true,
                shouldTouch: true
              }
            );
          }}
        >
          <Select.Option id={`${rubricItem.id}-no-score`} value="">
            - No Score -
          </Select.Option>
          {rubricItem.testRubricItemOptions.map((testRubricItemOption) => (
            <Select.Option
              key={testRubricItemOption.id}
              value={testRubricItemOption.identifier}
              id={testRubricItemOption.id.toString()}
            >
              {sanitizeText(testRubricItemOption.description)}
            </Select.Option>
          ))}
        </Select>
      </TooltipItem>
      {formState.errors.markItems && (
        <ErrorMessage
          errors={formState.errors}
          name={`markItems.${markItemIndex}.root`}
          className={styles.errorMessage}
        />
      )}
    </div>
  );
}
